// src/utils/firebaseQueries.js

import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { format, startOfMonth, endOfMonth } from 'date-fns';

/**
 * Lädt alle Terminarten des Tenant.
 */
export async function loadAppointmentTypesFromDB(tenantId) {
  const appointmentTypesSnapshot = await getDocs(
    collection(db, 'tenants', tenantId, 'appointmentTypes')
  );
  return appointmentTypesSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  }));
}

/**
 * Lädt alle Mitarbeiter des Tenant.
 */
export async function loadEmployeesFromDB(tenantId) {
  const employeesSnapshot = await getDocs(
    collection(db, 'tenants', tenantId, 'employees')
  );
  return employeesSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  }));
}

/**
 * Lädt Verfügbarkeiten (aus Collection 'availability'),
 * Abwesenheiten und nicht-stornierte Termine für EIN Datum (date).
 * Achtung: 'availability' hat offenbar kein Datumsfeld,
 * daher laden wir sie ungefiltert und filtern später anhand der Wochentage.
 */
export async function fetchDataForDate(tenantId, date, appointmentTypeId, selectedEmployee) {
  const dateStr = format(date, 'yyyy-MM-dd');

  // 1) Availability -> alle Dokumente, weil wir wochentagsbasiert filtern
  const availabilitySnapshot = await getDocs(
    collection(db, 'tenants', tenantId, 'availability')
  );
  const availabilities = availabilitySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  }));

  // 2) Absences -> hier nehmen wir an, dass ein Feld 'date' existiert
  const absencesQuery = query(
    collection(db, 'tenants', tenantId, 'absences'),
    where('date', '==', dateStr)
  );
  const absencesSnapshot = await getDocs(absencesQuery);
  const absences = absencesSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  }));

  // 3) Appointments (nicht stornierte) -> für das gegebene Datum
  const appointmentsQuery = query(
    collection(db, 'tenants', tenantId, 'appointments'),
    where('date', '==', dateStr),
    where('canceled', '==', false)
  );
  const appointmentsSnapshot = await getDocs(appointmentsQuery);
  const appointments = appointmentsSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  }));

  return { availabilities, absences, appointments };
}

/**
 * NEU: fetchDataForMonth(tenantId, monthDate)
 * Lädt Verfügbarkeiten (ungefiltert, aus 'availability'),
 * Abwesenheiten und nicht-stornierte Termine für [monthStart .. monthEnd].
 * 
 * So hast du nur EINE Abfrage pro Collection pro Monat,
 * statt für jeden Tag separate Queries.
 */
export async function fetchDataForMonth(tenantId, monthDate) {
  // 1) Start/End des Monats
  const monthStart = startOfMonth(monthDate);
  const monthEnd = endOfMonth(monthStart);

  // 2) Format für Firestore-String-Vergleich
  const startStr = format(monthStart, 'yyyy-MM-dd');
  const endStr = format(monthEnd, 'yyyy-MM-dd');

  // A) Availability (keine Datum-Filter)
  const availabilitySnapshot = await getDocs(
    collection(db, 'tenants', tenantId, 'availability')
  );
  const monthAvailabilities = availabilitySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  }));

  // B) Absences -> wo `date` >= startStr und <= endStr
  const absencesRef = collection(db, 'tenants', tenantId, 'absences');
  const absencesQuery = query(
    absencesRef,
    where('date', '>=', startStr),
    where('date', '<=', endStr)
  );
  const absencesSnapshot = await getDocs(absencesQuery);
  const monthAbsences = absencesSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  }));

  // C) Appointments (nicht storniert) -> [startStr..endStr]
  const appointmentsRef = collection(db, 'tenants', tenantId, 'appointments');
  const appointmentsQuery = query(
    appointmentsRef,
    where('date', '>=', startStr),
    where('date', '<=', endStr),
    where('canceled', '==', false)
  );
  const appointmentsSnapshot = await getDocs(appointmentsQuery);
  const monthAppointments = appointmentsSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  }));

  return {
    monthAvailabilities,
    monthAbsences,
    monthAppointments,
  };
}
